import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  HardDrive,
  Database,
  TrendingUp,
  AlertTriangle,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adminCapacityApi } from '../../../shared/services/adminCapacityApi'
import { AdminHeader } from '../../../shared/components'
import CollectionDataModal from '../components/capacity/CollectionDataModal'
import type { CapacityData } from '../components/capacity/types'

const CapacityManagementPage: React.FC = () => {
  const [capacityData, setCapacityData] = useState<CapacityData | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedCollection, setSelectedCollection] = useState('')
  const [clearingCollection, setClearingCollection] = useState<string | null>(
    null
  )

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    await fetchCapacityData()
    setLastUpdated(new Date())
  }

  const fetchCapacityData = async () => {
    try {
      setError('')
      // 실패는 서비스가 예외로 올린다
      const data = await adminCapacityApi.current()

      setCapacityData({
        database: data.database,
        disk: data.disk,
        growth: data.growth || {
          dailyGrowth: 0,
          weeklyGrowth: 0,
          monthlyGrowth: 0,
          dailyGrowthFormatted: '0 MB/day',
          weeklyGrowthFormatted: '0 MB/week',
          monthlyGrowthFormatted: '0 MB/month',
        },
      })
    } catch (error: any) {
      console.error('용량 데이터 조회 실패:', error)
      setError('용량 데이터 조회 실패')
      toast.error('용량 데이터를 불러올 수 없습니다')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    try {
      setRefreshing(true)
      await loadData()
      toast.success('데이터가 새로고침되었습니다')
    } catch {
      toast.error('새로고침에 실패했습니다')
    } finally {
      setRefreshing(false)
    }
  }

  const handleClearCollection = async (collectionName: string) => {
    // 보호된 컬렉션 확인
    const protectedCollections = ['users', 'admin_users']
    if (protectedCollections.includes(collectionName)) {
      toast.error('이 컬렉션은 보호되어 초기화할 수 없습니다')
      return
    }

    const confirmMessage = `정말로 '${collectionName}' 컬렉션의 모든 데이터를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다!`

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setClearingCollection(collectionName)

      const deletedCount =
        await adminCapacityApi.clearCollection(collectionName)

      toast.success(
        `${collectionName} 컬렉션이 초기화되었습니다 (${deletedCount}개 문서 삭제)`
      )
      // 데이터 새로고침
      await loadData()
    } catch (error: any) {
      console.error('컬렉션 초기화 실패:', error)
      toast.error(error.message || '컬렉션 초기화에 실패했습니다')
    } finally {
      setClearingCollection(null)
    }
  }

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) {
      return 'text-red-600'
    }
    if (percentage >= 80) {
      return 'text-yellow-600'
    }
    return 'text-green-600'
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            용량 데이터 로딩 중...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader
        title="용량 관리"
        subtitle="데이터베이스 및 디스크 사용량을 모니터링하고 예측합니다"
        showRefresh={true}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        lastUpdated={lastUpdated}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20"
          >
            <div className="flex">
              <AlertTriangle className="mr-2 h-5 w-5 text-red-400" />
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          </motion.div>
        )}

        {/* Summary Cards */}
        {capacityData && (
          <>
            <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Database className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                          데이터베이스 크기
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {capacityData.database.totalSizeFormatted}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800"
              >
                <div className="p-5">
                  <div className="mb-3 flex items-center">
                    <div className="flex-shrink-0">
                      <HardDrive className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        디스크 사용량
                      </h3>
                    </div>
                  </div>

                  {/* 프로그레스 바 */}
                  <div className="mb-3">
                    <div className="mb-1 flex justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>
                        {capacityData.disk.usedFormatted} /{' '}
                        {capacityData.disk.totalFormatted}
                      </span>
                      <span
                        className={`font-medium ${getUsageColor(capacityData.disk.usagePercentage)}`}
                      >
                        {capacityData.disk.usagePercentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          capacityData.disk.usagePercentage >= 90
                            ? 'bg-red-500'
                            : capacityData.disk.usagePercentage >= 80
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                        }`}
                        style={{
                          width: `${Math.min(capacityData.disk.usagePercentage, 100)}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* 여유 공간 정보 */}
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    여유 공간:{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {capacityData.disk.freeFormatted}
                    </span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                          일일 증가율
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {capacityData.growth.dailyGrowthFormatted ||
                            '0 MB/day'}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* 용량 예측 모델 */}
            {capacityData.growth.predictions && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mb-8 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 p-6 shadow dark:from-gray-800 dark:to-gray-700"
              >
                <h3 className="mb-4 flex items-center text-lg font-medium text-gray-900 dark:text-white">
                  <TrendingUp className="mr-2 h-5 w-5 text-indigo-600" />
                  용량 증가 예측 모델
                </h3>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
                    <div className="mb-1 text-sm text-gray-600 dark:text-gray-400">
                      1개월 후
                    </div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {capacityData.growth.predictions.oneMonth.sizeFormatted}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {capacityData.growth.predictions.oneMonth.date}
                    </div>
                  </div>

                  <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
                    <div className="mb-1 text-sm text-gray-600 dark:text-gray-400">
                      3개월 후
                    </div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {
                        capacityData.growth.predictions.threeMonths
                          .sizeFormatted
                      }
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {capacityData.growth.predictions.threeMonths.date}
                    </div>
                  </div>

                  <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
                    <div className="mb-1 text-sm text-gray-600 dark:text-gray-400">
                      6개월 후
                    </div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {capacityData.growth.predictions.sixMonths.sizeFormatted}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {capacityData.growth.predictions.sixMonths.date}
                    </div>
                  </div>

                  <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
                    <div className="mb-1 text-sm text-gray-600 dark:text-gray-400">
                      1년 후
                    </div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {capacityData.growth.predictions.oneYear.sizeFormatted}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {capacityData.growth.predictions.oneYear.date}
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    💡 현재 일일 증가율{' '}
                    <strong>{capacityData.growth.dailyGrowthFormatted}</strong>
                    을 기준으로 한 선형 예측입니다. 실제 증가율은 서비스
                    이용량에 따라 변동될 수 있습니다.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Detailed Stats */}
            <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* 디스크 사용량 상세 정보 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
              >
                <h3 className="mb-4 flex items-center text-lg font-medium text-gray-900 dark:text-white">
                  <HardDrive className="mr-2 h-5 w-5 text-green-600" />
                  디스크 상세 정보
                </h3>

                <div className="space-y-4">
                  {/* 전체 용량 */}
                  <div className="flex items-center justify-between border-b border-gray-200 py-2 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      총 용량
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {capacityData.disk.totalFormatted}
                    </span>
                  </div>

                  {/* 사용된 용량 */}
                  <div className="flex items-center justify-between border-b border-gray-200 py-2 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      사용된 용량
                    </span>
                    <span
                      className={`text-sm font-medium ${getUsageColor(capacityData.disk.usagePercentage)}`}
                    >
                      {capacityData.disk.usedFormatted}
                    </span>
                  </div>

                  {/* 여유 공간 */}
                  <div className="flex items-center justify-between border-b border-gray-200 py-2 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      여유 공간
                    </span>
                    <span className="text-sm font-medium text-green-600">
                      {capacityData.disk.freeFormatted}
                    </span>
                  </div>

                  {/* MongoDB 데이터 비율 */}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      MongoDB 데이터
                    </span>
                    <span className="text-sm font-medium text-blue-600">
                      {capacityData.database.totalSizeFormatted}
                      <span className="ml-1 text-xs text-gray-500">
                        (
                        {(
                          (capacityData.database.totalSize /
                            capacityData.disk.used) *
                          100
                        ).toFixed(1)}
                        %)
                      </span>
                    </span>
                  </div>

                  {/* 상태 인디케이터 */}
                  <div className="mt-4 rounded-md bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="flex items-center">
                      <div
                        className={`mr-2 h-3 w-3 rounded-full ${
                          capacityData.disk.usagePercentage >= 90
                            ? 'bg-red-500'
                            : capacityData.disk.usagePercentage >= 80
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                        }`}
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {capacityData.disk.usagePercentage >= 90
                          ? '위험: 디스크 공간이 부족합니다'
                          : capacityData.disk.usagePercentage >= 80
                            ? '주의: 디스크 공간을 확인하세요'
                            : '정상: 충분한 디스크 공간'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* 컬렉션별 상세 정보 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
              >
                <h3 className="mb-4 flex items-center text-lg font-medium text-gray-900 dark:text-white">
                  <Database className="mr-2 h-5 w-5 text-blue-600" />
                  데이터베이스 구성
                </h3>

                <div className="space-y-3">
                  {/* 데이터 vs 인덱스 비율 */}
                  <div className="mb-4">
                    <div className="mb-2 flex justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>데이터 / 인덱스</span>
                      <span>
                        {capacityData.database.dataSizeFormatted} /{' '}
                        {capacityData.database.indexSizeFormatted}
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                      <div className="flex h-2 overflow-hidden rounded-full">
                        <div
                          className="bg-blue-500"
                          style={{
                            width: `${(capacityData.database.dataSize / capacityData.database.totalSize) * 100}%`,
                          }}
                        />
                        <div
                          className="bg-purple-500"
                          style={{
                            width: `${(capacityData.database.indexSize / capacityData.database.totalSize) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <div className="mr-1 h-2 w-2 rounded-full bg-blue-500" />
                        데이터
                      </span>
                      <span className="flex items-center">
                        <div className="mr-1 h-2 w-2 rounded-full bg-purple-500" />
                        인덱스
                      </span>
                    </div>
                  </div>

                  {/* Top 5 컬렉션 */}
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      주요 컬렉션 (크기순)
                    </h4>
                    <div className="space-y-2">
                      {capacityData.database.collections
                        .sort((a, b) => b.size - a.size)
                        .slice(0, 5)
                        .map(collection => (
                          <div
                            key={collection.name}
                            className="flex justify-between text-sm"
                          >
                            <span className="truncate text-gray-600 dark:text-gray-400">
                              {collection.name}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {collection.sizeFormatted}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* 전체 컬렉션 테이블 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
            >
              <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                컬렉션별 상세 정보
              </h3>

              {capacityData.database.collections.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                          컬렉션
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                          크기
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                          문서 수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                          평균 문서 크기
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                          작업
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                      {capacityData.database.collections.map(collection => {
                        const isProtected = ['users', 'admin_users'].includes(
                          collection.name
                        )
                        const isClearing =
                          clearingCollection === collection.name

                        return (
                          <tr
                            key={collection.name}
                            className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                              <div className="flex items-center">
                                <span
                                  className={`${collection.documents > 0 ? 'cursor-pointer hover:text-blue-600' : ''}`}
                                  onClick={() => {
                                    if (collection.documents > 0) {
                                      setSelectedCollection(collection.name)
                                      setModalOpen(true)
                                    }
                                  }}
                                >
                                  {collection.name}
                                </span>
                                {collection.documents > 0 && (
                                  <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                                    📄 데이터 보기
                                  </span>
                                )}
                                {isProtected && (
                                  <span className="ml-2 rounded bg-green-100 px-2 py-1 text-xs text-green-600 dark:bg-green-900/20 dark:text-green-400">
                                    🔒 보호됨
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                              {collection.sizeFormatted}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                              {collection.documents.toLocaleString()}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                              {(collection.avgDocSize / 1024).toFixed(2)} KB
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                              {!isProtected && collection.documents > 0 && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleClearCollection(collection.name)
                                  }}
                                  disabled={isClearing}
                                  className="inline-flex items-center rounded border border-transparent bg-red-100 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
                                  title={`${collection.name} 컬렉션 초기화`}
                                >
                                  {isClearing ? (
                                    <>
                                      <div className="mr-1 h-3 w-3 animate-spin rounded-full border-b-2 border-red-600"></div>
                                      삭제 중...
                                    </>
                                  ) : (
                                    <>
                                      <Trash2 className="mr-1 h-3 w-3" />
                                      초기화
                                    </>
                                  )}
                                </button>
                              )}
                              {isProtected && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  보호된 컬렉션
                                </span>
                              )}
                              {!isProtected && collection.documents === 0 && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  데이터 없음
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  컬렉션 데이터를 불러올 수 없습니다.
                </p>
              )}
            </motion.div>
          </>
        )}
      </div>

      {/* 컬렉션 데이터 모달 */}
      <CollectionDataModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setSelectedCollection('')
        }}
        collectionName={selectedCollection}
      />
    </div>
  )
}

export default CapacityManagementPage
